import { Link, useNavigate } from "react-router-dom";
import { LANG_NAMES, SUPPORTED_LANGS, useLang } from "@/lib/i18n.jsx";
import { marketingHref, marketingLangUrl } from "@/lib/localePath";
import { isAuthed, logout as apiLogout, getUser, isPlatformStaff } from "@/lib/api";
import { isNativeApp } from "@/lib/platform";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, Globe, Menu, User } from "lucide-react";
import { useRef, useState } from "react";
import NotificationBell from "@/components/NotificationBell";

function LangToggle() {
  const { t, lang, setLang } = useLang();
  // La tendina si chiude da sola dopo la scelta: `<details>` resta aperto, e
  // sulle rotte tecniche — dove `pick` non naviga da nessuna parte — il menu
  // rimarrebbe sopra la barra senza motivo.
  const box = useRef(null);
  // L'indirizzo si legge da `window.location` e non da `useLocation()`: la
  // barra e' montata anche fuori da un router (nei test di pagina il router e'
  // sostituito da un finto parziale), e un hook che manca non deve impedire di
  // mostrare il selettore. I tre campi che servono — pathname, search, hash —
  // sono gli stessi.
  const location = typeof window !== "undefined" ? window.location : null;
  // Sulla superficie pubblica la lingua **e' un indirizzo**: `/it/prezzi` e
  // `/en/pricing` sono due pagine, non la stessa pagina colorata diversamente,
  // e chi cambia lingua deve finire su quella giusta con una navigazione vera
  // (il canonical e i meta li scrive il server per quell'indirizzo). Sulle
  // rotte tecniche — dashboard, area ospiti, area commerciante — l'indirizzo
  // non cambia e si aggiorna solo la lingua: portare un host dal calendario a
  // `/en/accommodations` sarebbe una navigazione che non ha chiesto.
  const pick = (code) => {
    if (box.current) box.current.open = false;
    const url = marketingLangUrl(location, code);
    setLang(code);
    if (url) window.location.assign(url);
  };
  // Le lingue sono quelle del dizionario, e sono sette: una per una in barra
  // erano una pillola larga ~180px che su un telefono spingeva fuori tutto il
  // resto. Qui resta solo la lingua in uso (`IT`) e il resto vive nel menu, con
  // il nome **nella propria lingua**: chi cerca «Deutsch» non deve sapere che
  // cosa significa `DE`. `<details>` e non uno stato nostro, come il menu di
  // accesso qui sopra: si apre e si chiude senza un click fuori da gestire.
  return (
    <details ref={box} className="relative" data-testid="lang-toggle">
      <summary
        data-testid="lang-toggle-trigger"
        aria-label={`${(typeof t === "function" ? t("nav.language") : "Lingua")}: ${LANG_NAMES[lang] || lang}`}
        title={LANG_NAMES[lang] || lang}
        className="list-none [&::-webkit-details-marker]:hidden cursor-pointer select-none inline-flex items-center gap-1 rounded-full border border-border pl-2 pr-1.5 py-1.5 text-[11px] font-medium text-foreground hover:bg-accent lift"
      >
        <Globe className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
        <span data-testid="lang-current">{lang.toUpperCase()}</span>
        <ChevronDown className="h-3 w-3 opacity-60" aria-hidden="true" />
      </summary>
      <div
        data-testid="lang-menu"
        className="absolute right-0 top-full mt-1 w-40 rounded-lg border border-border bg-card shadow-lg overflow-hidden z-20"
      >
        {SUPPORTED_LANGS.map((code) => (
          <button
            key={code}
            type="button"
            data-testid={`lang-${code}`}
            onClick={() => pick(code)}
            lang={code}
            aria-pressed={lang === code}
            className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[12px] hover:bg-primary/5 ${lang === code ? "font-semibold text-primary bg-primary/5" : "text-foreground"}`}
          >
            <span>{LANG_NAMES[code] || code}</span>
            {lang === code && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
          </button>
        ))}
      </div>
    </details>
  );
}

export default function Navbar({ variant = "marketing" }) {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const authed = isAuthed();
  const user = authed ? getUser() : null;
  const isGuest = user?.role === "guest";
  // Un commerciante non è un host: non ha una struttura, non ha un gestionale
  // e la dashboard lo manderebbe in un vicolo cieco. La sua destinazione è lo
  // spazio commercianti, e la barra gliela mostra al posto di quella host.
  const isMerchant = user?.role === "local_partner";
  const staff = isPlatformStaff();
  // Le tre porte della piattaforma: superuser/delega, ruolo superhost, flag
  // superhost. Chi amministra vede la sezione commercianti anche dentro il
  // prodotto host, e la apre sulla console invece che sulla pagina pubblica.
  const isPlatformAdmin = staff || user?.role === "superhost" || !!user?.is_superhost;
  const merchantPath = isPlatformAdmin ? "/commercianti/admin" : marketingHref("merchants", lang);
  const dashPath = isGuest
    ? "/guest/dashboard"
    : isMerchant
      ? "/commercianti/area"
      : "/dashboard?tab=calendar";
  const dashLabel = isGuest
    ? (t("guest.my_bookings") || "Le mie prenotazioni")
    : isMerchant
      ? t("nav.merchant_area")
      : t("nav.dashboard");

    const inHostProduct = variant === "dashboard";

  // «Tariffe», «Trade B2B» e «Rivendica» sono le tre porte del prodotto *host*:
  // la tariffa è quella che il viaggiatore paga, il B2B è di chi vende camere
  // all'ingrosso, la rivendicazione è di chi ha una struttura e non ha ancora un
  // account. Nella barra dell'area viaggiatori non portano da nessuna parte —
  // chi è li' dentro ha già un ruolo, ed è l'altro. Restano sul sito pubblico,
  // dove servono a raccontare il prodotto a chi un ruolo non ce l'ha ancora.
  const showHostLinks = !isGuest && !isMerchant;

  //: Nell'app c'è solo la reception: i link del sito — cerca, tariffe, B2B,
  //: rivendica, affiliati — aprirebbero pagine web dentro un guscio da telefono,
  //: dove nessuno le usa (vedi `docs/COMPANION-NATIVO.md`). Restano il logo,
  //: il pulsante della dashboard, l'account e l'uscita: quelli servono anche lì.
  const showSiteLinks = !isNativeApp();

  const linkCls = "text-sm text-foreground/80 hover:text-primary transition-colors";

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 border-b border-border/60 ${variant === "marketing" ? "backdrop-blur-xl bg-background/70" : "bg-background"}`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-3">
        {/* Nell'app il logo riporta alla reception, non alla home del sito:
            l'indirizzo del marketing dentro il guscio non porta da nessuna parte. */}
        <Link to={isNativeApp() ? "/dashboard" : marketingHref("home", lang)} data-testid="nav-logo" className="flex items-center gap-2 group min-w-0">
          <span className="w-8 h-8 shrink-0 rounded-md bg-primary text-primary-foreground grid place-items-center font-display text-lg">H</span>
          <span className="font-display text-xl sm:text-2xl tracking-tight leading-none truncate">Hostaly</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {showSiteLinks && !inHostProduct && (
            <Link to="/cerca" className={linkCls} data-testid="nav-search">{t("nav.find_stay")}</Link>
          )}
          {showSiteLinks && showHostLinks && (
            <Link to={marketingHref("pricing", lang)} className={linkCls} data-testid="nav-pricing">{t("nav.pricing") || "Tariffe"}</Link>
          )}
          {showSiteLinks && showHostLinks && (
            <Link to="/trade" className={linkCls} data-testid="nav-trade">{t("dash.trade_nav")}</Link>
          )}
          {/* Il bedbank è un canale B2B wholesale, non una funzione di ogni
              host: chi apre la dashboard del proprio B&B non ha un blocco di
              camere da mettere in fornitura, e una voce di menu che non
              usa altro che genera domande. Resta raggiungibile per URL
              diretto quando si arruola un fornitore. */}
          {showSiteLinks && staff && (
            <Link to="/bedbank" className={linkCls} data-testid="nav-bedbank">
              {t("nav.bedbank")}
            </Link>
          )}
          {/* La rivendicazione era raggiungibile solo dal link nelle email e
              dal bottone su una singola scheda: chi non ha ricevuto l'email
              non sapeva che esistesse.

              Visibile a tutti, non solo a chi ha un account: il passo 1 — la
              ricerca — e' pubblico per volo', e la domanda "esiste gia' la mia
              scheda?" non deve chiedere il login. Se la richiedesse, chi cerca
              la propria scheda senza account andrebbe a cercarsela da solo su
              Booking, che e' esattamente il posto da cui vogliamo tirarlo
              dentro. Il login viene chiesto al passo 3, quando serve. */}
          {showSiteLinks && showHostLinks && (
            <Link to="/rivendica" className={linkCls} data-testid="nav-rivendica">
              {t("nav.claim")}
            </Link>
          )}
          {showSiteLinks && (
            <Link to="/affiliati" className={linkCls} data-testid="nav-affiliates">
              {t("nav.affiliates")}
            </Link>
          )}
          {isGuest && (
            <Link to="/guest/dashboard#mappe" className={linkCls} data-testid="nav-my-maps">
              {t("guest.my_maps") || "Le mie mappe"}
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <LangToggle />
          {authed ? (
            <>
              <Button asChild variant="ghost" size="sm" data-testid="nav-account-btn" className="hidden sm:inline-flex">
                <Link to="/account"><User className="h-4 w-4 mr-1"/>{t("nav.account")}</Link>
              </Button>
              <NotificationBell/>
              <Button asChild variant="ghost" size="sm" data-testid="nav-dashboard-btn" className="hidden sm:inline-flex">
                <Link to={dashPath}>{dashLabel}</Link>
              </Button>
              <Button
                variant="outline" size="sm" className="lift hidden sm:inline-flex"
                data-testid="nav-logout-btn"
                onClick={async () => { await apiLogout(); nav("/"); }}
              >{t("nav.logout")}</Button>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" className="hidden md:inline-flex" data-testid="nav-book-btn">
                <Link to={marketingHref("directory", lang)}>{t("nav.book") || (t("nav.directory") ? `${t("nav.directory")} →` : "Prenota")}</Link>
              </Button>
              {/* Visibile anche in verticale sul telefono. Prima era
                  `hidden sm:inline-flex`: sotto i 640 px — la larghezza di un
                  telefono tenuto in mano — sparivano insieme il menu delle tre
                  porte e «Registrati», e le uniche porte restavano quelle
                  generiche dentro l'hamburger. Ruotando lo schermo i 640 px
                  arrivavano e tutto ricompariva: era questo a far sembrare il
                  difetto una questione di orientamento. */}
              <div className="relative inline-flex" data-testid="nav-login-menu">
                <details className="group">
                  <summary className="list-none cursor-pointer">
                    <span className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md hover:bg-primary/5 border border-transparent hover:border-border" data-testid="nav-login-btn">
                      {t("nav.login")}
                      <svg width="10" height="6" viewBox="0 0 10 6" className="opacity-70"><path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>
                    </span>
                  </summary>
                  <div className="absolute right-0 top-full mt-1 w-64 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-10" data-testid="nav-login-dropdown">
                    <Link to="/guest/auth?mode=login" className="block px-4 py-3 hover:bg-primary/5 border-b border-border" data-testid="nav-login-guest">
                      <div className="text-sm font-semibold">{t("guest.travellers_area") || "Sei un viaggiatore"}</div>
                      <div className="text-[11px] text-muted-foreground">{t("guest.my_bookings") || "Le mie prenotazioni"}</div>
                    </Link>
                    <Link to="/auth?mode=login" className="block px-4 py-3 border-b border-border hover:bg-primary/5" data-testid="nav-login-host">
                      <div className="text-sm font-semibold">{t("nav.host_area") || "Gestisco una struttura"}</div>
                      <div className="text-[11px] text-muted-foreground">{t("nav.host_dashboard")}</div>
                    </Link>
                    {/* La terza porta: un ristorante non è un host e non è un
                        viaggiatore. Senza questa voce finiva nel form che
                        chiede il CIN, che non ha. */}
                    <Link to="/commercianti/accesso" className="block px-4 py-3 hover:bg-primary/5" data-testid="nav-login-merchant">
                      <div className="text-sm font-semibold">{t("nav.merchant_login_title")}</div>
                      <div className="text-[11px] text-muted-foreground">{t("nav.merchant_space")}</div>
                    </Link>
                    {/* Chi non ha un account finiva qui a cercarsi «Registrati»,
                        che sul telefono era l'ultima voce dell'hamburger. Sta
                        dentro il menu che ha già aperto. */}
                    <Link to="/auth?mode=register" className="block px-4 py-3 border-t border-border bg-muted/30 hover:bg-primary/5" data-testid="nav-login-signup">
                      <div className="text-sm font-semibold">{t("nav.signup")}</div>
                      <div className="text-[11px] text-muted-foreground">{t("nav.host_register") || "Crea un account per la struttura"}</div>
                    </Link>
                  </div>
                </details>
              </div>
              <Button asChild size="sm" className="lift bg-primary text-primary-foreground hover:bg-primary/90 hidden sm:inline-flex" data-testid="nav-signup-btn">
                <Link to="/auth?mode=register">{t("nav.signup")}</Link>
              </Button>
            </>
          )}
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>
      {/* Il pannello cresce con le voci, e su un telefono piccolo (o con la
          barra degli indirizzi aperta) l'ultima riga — «Registrati» — finiva
          sotto il bordo senza modo di raggiungerla. Scorre dentro di sé invece
          di uscire dallo schermo. */}
      {open && (
        <div className="md:hidden border-t border-border/60 px-6 py-4 flex flex-col gap-3 bg-background max-h-[calc(100dvh-4rem)] overflow-y-auto">
          {showSiteLinks && !inHostProduct && <Link to="/cerca" className={linkCls} data-testid="nav-mobile-search">{t("nav.find_stay")}</Link>}
          {showSiteLinks && showHostLinks && (
            <Link to={marketingHref("pricing", lang)} className={linkCls} data-testid="nav-mobile-pricing">{t("nav.pricing") || "Tariffe"}</Link>
          )}
          {showSiteLinks && showHostLinks && (
            <Link to="/trade" className={linkCls} data-testid="nav-mobile-trade">{t("dash.trade_nav")}</Link>
          )}
          {showSiteLinks && staff && <Link to="/bedbank" className={linkCls} data-testid="nav-mobile-bedbank">Bedbank</Link>}
          {showSiteLinks && showHostLinks && (
            <Link to="/rivendica" className={linkCls} data-testid="nav-mobile-rivendica">{t("nav.claim")}</Link>
          )}
          {showSiteLinks && <Link to="/affiliati" className={linkCls} data-testid="nav-mobile-affiliates">{t("nav.affiliates")}</Link>}
          {showSiteLinks && (!inHostProduct || isPlatformAdmin) && (
            <Link to={merchantPath} className={linkCls} data-testid="nav-mobile-merchants">{t("nav.merchants")}</Link>
          )}
          {isGuest && <Link to="/guest/dashboard#mappe" className={linkCls}>{t("guest.my_maps") || "Le mie mappe"}</Link>}
          <div className="pt-2 border-t border-border/60 flex flex-col gap-2">
            {authed && <Link to="/account" className={linkCls} data-testid="nav-mobile-account"><User className="h-4 w-4 mr-1 inline"/>{t("nav.account")}</Link>}
            {authed ? (
              <>
                <Button asChild variant="ghost" size="sm"><Link to={dashPath}>{dashLabel}</Link></Button>
                <Button variant="outline" size="sm" onClick={async () => { await apiLogout(); nav("/"); }}>{t("nav.logout")}</Button>
              </>
            ) : (
              <>
                {/* Le stesse tre porte del menu in alto, non un «Accedi» unico:
                    un viaggiatore che toccava quel bottone finiva nel modulo
                    dell'host, che gli chiede il CIN. */}
                <Link to="/guest/auth?mode=login" className={linkCls} data-testid="nav-mobile-login-guest">
                  {t("guest.travellers_area") || "Sei un viaggiatore"}
                </Link>
                <Link to="/auth?mode=login" className={linkCls} data-testid="nav-mobile-login-host">
                  {t("nav.host_area") || "Gestisco una struttura"}
                </Link>
                <Link to="/commercianti/accesso" className={linkCls} data-testid="nav-mobile-login-merchant">
                  {t("nav.merchant_login_title")}
                </Link>
                <Button asChild size="sm" className="mt-1 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="nav-mobile-signup">
                  <Link to="/auth?mode=register">{t("nav.signup")}</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
